/**
 * Swipeable Product Grid Variants for Mobile
 * Adds touch swipe functionality to change variants in product grids
 */

(function() {
  'use strict';

  const SWIPE_THRESHOLD = 50; // Minimum distance for a swipe
  const VELOCITY_THRESHOLD = 0.3; // Minimum velocity for a swipe
  const MOBILE_BREAKPOINT = 768;

  class SwipeableVariants {
    constructor(container) {
      this.container = container;
      this.touchStartX = 0;
      this.touchStartTime = 0;
      this.currentX = 0;
      this.isDragging = false;
      this.currentVariantIndex = 0;
      
      // Get variant elements
      this.variantInputs = container.querySelectorAll('.variant-input input[type="radio"]');
      this.imageContainer = container.querySelector('.grid-product__image-mask');
      this.variantImages = container.querySelectorAll('.variant-images img');
      
      if (!this.variantInputs.length || !this.imageContainer) return;
      
      // Only initialize on mobile
      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        this.init();
      }
      
      // Handle resize events
      this.handleResize = this.handleResize.bind(this);
      window.addEventListener('resize', this.handleResize);
    }

    init() {
      // Find currently selected variant
      this.updateCurrentVariantIndex();
      
      // Add touch event listeners
      this.imageContainer.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.imageContainer.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
      this.imageContainer.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
      
      // Add transition class for smooth animations
      this.variantImages.forEach(img => {
        img.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      });
      
      // Prevent default image dragging
      this.imageContainer.style.userSelect = 'none';
      this.imageContainer.style.webkitUserSelect = 'none';
      this.imageContainer.style.touchAction = 'pan-y pinch-zoom';
    }

    destroy() {
      this.imageContainer.removeEventListener('touchstart', this.handleTouchStart);
      this.imageContainer.removeEventListener('touchmove', this.handleTouchMove);
      this.imageContainer.removeEventListener('touchend', this.handleTouchEnd);
      
      // Reset styles
      this.imageContainer.style.userSelect = '';
      this.imageContainer.style.webkitUserSelect = '';
      this.imageContainer.style.touchAction = '';
    }

    handleResize() {
      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        if (!this.isDragging) {
          this.init();
        }
      } else {
        this.destroy();
      }
    }

    updateCurrentVariantIndex() {
      const checkedInput = Array.from(this.variantInputs).findIndex(input => input.checked);
      this.currentVariantIndex = checkedInput >= 0 ? checkedInput : 0;
    }

    handleTouchStart(e) {
      if (e.touches.length !== 1) return;
      
      this.touchStartX = e.touches[0].clientX;
      this.currentX = this.touchStartX;
      this.touchStartTime = Date.now();
      this.isDragging = true;
      
      // Add dragging class for visual feedback
      this.imageContainer.classList.add('is-dragging');
    }

    handleTouchMove(e) {
      if (!this.isDragging || e.touches.length !== 1) return;
      
      this.currentX = e.touches[0].clientX;
      const deltaX = this.currentX - this.touchStartX;
      
      // Add subtle drag feedback
      const currentImage = this.variantImages[this.currentVariantIndex];
      if (currentImage && !currentImage.classList.contains('hidden')) {
        const dragScale = 1 - Math.abs(deltaX) / 1000;
        currentImage.style.transform = `translateX(${deltaX * 0.3}px) scale(${Math.max(0.95, dragScale)})`;
        currentImage.style.opacity = Math.max(0.3, 1 - Math.abs(deltaX) / 200);
      }
      
      // Prevent vertical scrolling while swiping horizontally
      if (Math.abs(deltaX) > 10 && e.cancelable) {
        e.preventDefault();
      }
    }

    handleTouchEnd(e) {
      if (!this.isDragging) return;
      
      this.isDragging = false;
      this.imageContainer.classList.remove('is-dragging');
      
      const deltaX = this.currentX - this.touchStartX;
      const deltaTime = Date.now() - this.touchStartTime;
      const velocity = Math.abs(deltaX) / deltaTime;
      
      // Reset current image transform
      const currentImage = this.variantImages[this.currentVariantIndex];
      if (currentImage) {
        currentImage.style.transform = '';
        currentImage.style.opacity = '';
      }
      
      // Determine if it's a valid swipe
      if (Math.abs(deltaX) > SWIPE_THRESHOLD || velocity > VELOCITY_THRESHOLD) {
        if (deltaX > 0) {
          // Swipe right - previous variant
          this.selectPreviousVariant();
        } else {
          // Swipe left - next variant
          this.selectNextVariant();
        }
      }
    }

    selectNextVariant() {
      const enabledInputs = Array.from(this.variantInputs).filter(input => !input.disabled);
      if (enabledInputs.length <= 1) return;
      
      const currentEnabledIndex = enabledInputs.findIndex(input => input.checked);
      const nextIndex = (currentEnabledIndex + 1) % enabledInputs.length;
      
      this.selectVariant(enabledInputs[nextIndex]);
    }

    selectPreviousVariant() {
      const enabledInputs = Array.from(this.variantInputs).filter(input => !input.disabled);
      if (enabledInputs.length <= 1) return;
      
      const currentEnabledIndex = enabledInputs.findIndex(input => input.checked);
      const prevIndex = (currentEnabledIndex - 1 + enabledInputs.length) % enabledInputs.length;
      
      this.selectVariant(enabledInputs[prevIndex]);
    }

    selectVariant(input) {
      if (!input || input.disabled) return;
      
      // Trigger click to maintain compatibility with existing code
      input.click();
      
      // Update current index
      this.updateCurrentVariantIndex();
      
      // Add animation class
      this.imageContainer.classList.add('variant-changing');
      setTimeout(() => {
        this.imageContainer.classList.remove('variant-changing');
      }, 300);
    }
  }

  // Initialize on DOM ready
  function initSwipeableGrids() {
    // Only run on collection/search pages
    if (!document.querySelector('.products-grid')) return;
    
    // Initialize for all product grid items
    const gridItems = document.querySelectorAll('.grid__item.grid-product');
    
    gridItems.forEach(item => {
      // Check if item has multiple variants
      const variantInputs = item.querySelectorAll('.variant-input input[type="radio"]');
      if (variantInputs.length > 1) {
        new SwipeableVariants(item);
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSwipeableGrids);
  } else {
    initSwipeableGrids();
  }

  // Re-initialize when new products are loaded (infinite scroll)
  document.addEventListener('collection:pushup', initSwipeableGrids);
  
  // Listen for infinite scroll updates
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.classList && node.classList.contains('grid-product')) {
            const variantInputs = node.querySelectorAll('.variant-input input[type="radio"]');
            if (variantInputs.length > 1) {
              new SwipeableVariants(node);
            }
          }
        });
      }
    });
  });

  // Observe the products grid for changes
  const productsGrid = document.querySelector('.products-grid');
  if (productsGrid) {
    observer.observe(productsGrid, {
      childList: true,
      subtree: true
    });
  }

})();
